import { FC } from 'react';
import {
  Alert,
  Box,
  Grid,
  GridItem,
  HStack,
  SkeletonText,
  Text,
  VStack
} from '@chakra-ui/react';
import { Link, useParams } from 'react-router-dom';
import { Proposal, Space, Vote } from 'types/snapshot';
import { useQuery } from 'react-query';
import { getProposal, getResults } from 'api/snapshot';
import { IoIosArrowRoundBack } from 'react-icons/io';
import { RiFileWarningLine } from 'react-icons/ri';
import { useProfiles } from 'hooks/useProfiles';
import ProposalInformation from 'components/proposals/ProposalInformation';
import ProposalSummary from 'components/proposals/ProposalSummary';
import ProposalCard from 'components/shared/ProposalCard';
import VotesList from 'components/proposals/VotesList';
import ProposalResults from 'components/proposals/ProposalResults';
import VotingFormContainer from 'components/proposals/VotingFormContainer';

// TODO: move these to snapshot return types
export type VoteExtended = {
  balance: number;
  choice: number[];
  created: number;
  id: string;
  scores: number[];
  voter: string;
  voterEns?: string;
};

export type ProposalResponse = {
  proposal: Proposal;
  votes: Vote[];
  space: Space;
};

export type ResultsResponse = {
  results: {
    resultsByStrategyScore: number[];
    resultsByVoteBalance: number[];
    sumOfResultsBalance: number;
  };
  votes: VoteExtended[];
  space: Space;
};

const ProposalDetail: FC = () => {
  const { proposalId } = useParams<{ proposalId: string }>();
  const { spaceId } = useParams<{ spaceId: string }>();

  const { data: proposal, isLoading: isLoadingProposal } =
    useQuery<ProposalResponse>(['proposal', proposalId], () =>
      getProposal({
        id: proposalId,
        spaceName: spaceId
      })
    );

  const {
    data: results,
    // error: errorResults,
    refetch: refetchResults
  } = useQuery<ResultsResponse>(
    ['results', proposalId],
    () =>
      getResults({
        space: proposal!.space,
        proposal: proposal!.proposal,
        votes: proposal!.votes
      }),
    {
      enabled: !!proposal
    }
  );

  useProfiles(proposal, results, refetchResults);

  if (!isLoadingProposal && !proposal?.proposal) {
    return (
      <Alert borderRadius='lg' mt={10}>
        <VStack>
          <RiFileWarningLine size={70} />
        </VStack>
        <VStack spacing={4} pl={3} w='100%'>
          <Text fontWeight='600' fontSize='xl'>
            Proposal not found.
          </Text>
          <Text>This link may be out of date or broken.</Text>
          <Text>If you believe this is a mistake, please try again later.</Text>
        </VStack>
      </Alert>
    );
  }

  return (
    <Box>
      <Grid templateColumns='repeat(12, 1fr)' gap={[5, null, null, 8]}>
        <GridItem colSpan={[12, null, null, 8]}>
          <VStack spacing={[5, null, null, 8]} alignItems='stretch'>
            <Box>
              <Link to={`/governance/${proposal?.space.id}`}>
                <HStack>
                  <IoIosArrowRoundBack fontSize='32px' />
                  <Text>{proposal?.space.name} Proposals</Text>
                </HStack>
              </Link>
              <ProposalSummary proposal={proposal?.proposal} />
            </Box>
            {proposal && proposal.proposal.state === 'active' && (
              <ProposalCard heading='Cast your vote'>
                <VotingFormContainer proposal={proposal} />
              </ProposalCard>
            )}
            <ProposalCard
              heading='Votes'
              tag={results?.votes.length.toString()}
            >
              <VotesList results={results} proposal={proposal} />
            </ProposalCard>
          </VStack>
        </GridItem>
        <GridItem colSpan={[12, null, null, 4]} height='fit-content'>
          <VStack spacing={[5, null, null, 8]} alignItems='stretch'>
            <ProposalCard heading='Information'>
              <SkeletonText isLoaded={!!proposal} noOfLines={7} spacing={6}>
                {proposal && (
                  <ProposalInformation
                    proposal={proposal.proposal}
                    space={proposal.space}
                  />
                )}
              </SkeletonText>
            </ProposalCard>
            <ProposalCard
              heading={
                proposal?.proposal.state === 'active'
                  ? 'Current Results'
                  : 'Results'
              }
            >
              <SkeletonText isLoaded={!!results} noOfLines={2} spacing={6}>
                {results && proposal && (
                  <ProposalResults results={results} proposal={proposal} />
                )}
              </SkeletonText>
            </ProposalCard>
          </VStack>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default ProposalDetail;
